<script setup lang="ts">
import { inject, ref, onUnmounted } from 'vue';
import type { TableModel } from './model';
import type { IColumnDefinition } from './types';

const model = inject<TableModel>('model');
if (!model) {
  throw new Error('ItpDataTableHeader missing model');
}

const resizingColumn = ref<IColumnDefinition | null>(null);
const startX = ref(0);
const startWidth = ref(0);

const onMouseDown = (col: IColumnDefinition, event: MouseEvent) => {
  if (col.resizable === false || col.systemColumn) {
    return;
  }
  event.stopPropagation();
  event.preventDefault();
  resizingColumn.value = col;
  startX.value = event.pageX;
  const th = (event.target as HTMLElement).closest('th');
  if (th) {
    startWidth.value = th.offsetWidth;
  }
  document.addEventListener('mousemove', onMouseMove);
  document.addEventListener('mouseup', onMouseUp);
  document.body.style.cursor = 'col-resize';
  document.body.classList.add('user-select-none');
};
const onMouseMove = (event: MouseEvent) => {
  if (!resizingColumn.value) {
    return;
  }
  const diff = event.pageX - startX.value;
  const newWidth = Math.max(startWidth.value + diff, 40);
  resizingColumn.value.width = `${newWidth}px`;
};
const onMouseUp = () => {
  resizingColumn.value = null;
  document.removeEventListener('mousemove', onMouseMove);
  document.removeEventListener('mouseup', onMouseUp);
  document.body.style.cursor = '';
  document.body.classList.remove('user-select-none');
};
onUnmounted(() => {
  document.removeEventListener('mousemove', onMouseMove);
  document.removeEventListener('mouseup', onMouseUp);
});

const isColumnSortable = (col: IColumnDefinition) =>
  model.options.sortable !== false && col.sortable !== false;

const getSortIconClass = (col: IColumnDefinition) => {
  if (col.sort === 1) {
    return 'fa-sort-down';
  }
  if (col.sort === -1) {
    return 'fa-sort-up';
  }
  return 'fa-sort';
};

const onSort = (col: IColumnDefinition, event: MouseEvent) => {
  if (resizingColumn.value) {
    return;
  }
  if (isColumnSortable(col)) {
    model.sortRows(col, event.shiftKey);
  }
};

const getColumnStyle = (col: IColumnDefinition) => {
  let width = col.width;
  if (!width) {
    width = col.minWidth
      ? typeof col.minWidth === 'number'
        ? `${col.minWidth}px`
        : col.minWidth
      : '150px';
  }
  const style: Record<string, any> = {
    width,
    minWidth: col.minWidth,
    maxWidth: width,
  };
  if (col.frozen) {
    style.position = 'sticky';
    if (col.sticky === 'left') {
      style.left = 0;
      style.zIndex = 3;
    }
    if (col.sticky === 'right') {
      style.right = 0;
      style.zIndex = 3;
    }
  }
  if (isColumnSortable(col)) {
    style.cursor = 'pointer';
  }
  return style;
};

const getColumnClasses = (col: IColumnDefinition) => {
  const classes: Record<string, boolean> = {};
  if (col.frozen) {
    if (col.sticky === 'left') {
      classes['sticky-l'] = true;
    }
    if (col.sticky === 'right') {
      classes['sticky-r'] = true;
    }
  }
  let align = col.textAlign;
  if (!align) {
    const type = col.type;
    if (type === 'number') {
      align = 'right';
    } else {
      align = model.options.textAlign || 'left';
    }
  }
  if (align) {
    classes[`text-${align}`] = true;
  }
  return classes;
};
</script>

<template>
  <thead>
    <tr>
      <th
        v-if="model.options.rowSelection"
        class="itp-header-cell text-center sticky-l"
        style="width: 20px; min-width: 20px; z-index: 20"
      ></th>

      <th
        v-if="model.options.rowHeader"
        class="itp-header-cell sticky-l"
        :style="{ left: model.options.rowSelection ? '20px' : '0' }"
        style="width: 20px; min-width: 20px; z-index: 20"
      ></th>

      <th
        v-for="col in model.options.columns"
        :key="col.field"
        class="itp-header-cell font-size-smaller"
        :class="getColumnClasses(col)"
        :style="getColumnStyle(col)"
        @click="onSort(col, $event)"
      >
        <div
          class="d-flex w-100 align-items-center py-1 px-2"
          :class="{
            'justify-content-start': !col.textAlign || col.textAlign === 'left',
            'justify-content-center': col.textAlign === 'center',
            'justify-content-end': col.textAlign === 'right',
          }"
        >
          <div
            class="text-dark user-select-none text-truncate flex-grow-1"
            :title="col.header"
          >
            <i
              v-if="col.headerIcon"
              :class="'fas fa-' + col.headerIcon"
              class="me-1"
            />
            <span v-else>{{ col.header || col.name }}</span>
          </div>

          <i
            v-if="isColumnSortable(col)"
            class="fas ms-2"
            :class="[
              getSortIconClass(col),
              col.sort ? 'text-dark' : 'text-black-50',
            ]"
          />
        </div>

        <div
          v-if="!col.systemColumn && col.resizable !== false"
          class="column__resizer"
          @mousedown.stop.prevent="onMouseDown(col, $event)"
          @click.stop
        />
      </th>

      <th
        v-if="model.options.fillEmptyRowSpace !== false"
        class="itp-header-cell"
        :class="{ 'border-end-0': model.options.editable }"
        style="width: 100%"
      ></th>

      <th
        v-if="model.options.editable"
        class="itp-header-cell"
        style="width: 80px; min-width: 80px"
      ></th>
    </tr>
  </thead>
</template>
